import { useMutation, useQuery } from '@tanstack/react-query';
import { List, ListItem, Toggle } from 'framework7-react';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import { orderBy, uniq, update } from 'lodash';
import pluralize from 'pluralize';
import { KING_OF_DRAG_ENABLED } from 'src/constants/FeatureFlags';

import API from '@api/API';

import {
  EntertainerSchedule,
  useCurrentEntertainerQuery,
  useCurrentEntertainerScheduleQuery,
  useEntertainerDirectory,
} from '@queries/EntertainerQueries';
import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';
import { Series, useSeriesByTagSlug, useSeriesEntertainersQuery } from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { CurrentCity, useCitiesQuery, useCityById } from '@hooks/useCurrentUserLocation';

import {
  EntertainerDashboardAction,
  useLocation,
  useNavigate,
  useParams,
  useRouter,
} from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { Form } from '@components/form/Form';
import { FormLabel } from '@components/form/FormLabel';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { MultiSelectField } from '@screens/entertainer/components/CitiesField';
import { AddImageContent } from '@screens/entertainer/components/FeaturedPhotoManagementSection';
import { SeriesEntertainersList } from '@screens/series/SeriesScreen/SeriesScreen';

import magazineSvgUrl from '../SeriesScreen/magazine.svg';

const useUpdateSeriesApplicationMutation = (seriesId: string) => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: (
      updatedProperties: Partial<{
        expectedCompensation?: string;
        questions: Record<
          string,
          {
            question: string;
            answer: string;
            meta?: Record<string, any>;
          }
        >;
      }>,
    ) => {
      return API.post(`/v1/series/${seriesId}/application`, updatedProperties);
    },
  });

  return {
    isUpdatingSeriesApplication: isPending,
    updateSeriesApplicationAsync: mutateAsync,
  };
};

enum UserSeriesApplicationQuestion {
  AboutEntertainer = 'aboutEntertainer',
  AboutScene = 'aboutScene',
  SpotlightOne = 'spotlightOne',
  SpotlightTwo = 'spotlightTwo',
}

interface UserSeriesApplication {
  _id: string;
  entertainerMediaId: string;
  questions: Partial<
    Record<
      UserSeriesApplicationQuestion,
      {
        question: string;
        answer: string;
        placeholder?: string;
        meta?: Record<string, any>;
      }
    >
  >;
}

const useSeriesApplicationQuery = (seriesId: string) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['series', seriesId, 'application'],
    queryFn: async () => {
      const response = await API.get<{ userSeriesApplication?: UserSeriesApplication }>(
        `/v1/series/${seriesId}/application`,
      );
      return response.userSeriesApplication;
    },
    enabled: Boolean(seriesId),
  });

  return {
    userSeriesApplication: data,
    isUserSeriesApplicationReady: isFetched,
    refetchUserSeriesApplication: refetch,
  };
};

interface SeriesFeatureFormScreenProps {
  tagSlug: string;
}

const StepOne = ({
  series,
  userSeriesApplication,
  onSave,
}: {
  series: Series;
  userSeriesApplication?: UserSeriesApplication;
  onSave: () => void;
}) => {
  return (
    <>
      <TitleToolbar text="Step 1: Select your cover photo" size="sm" />

      <p className="mt-0">
        We need a cover photo for your feature in the Instagram post! Select one of your favorite
        looks and give it a title.
      </p>

      <AddImageContent
        cityId={series.city._id}
        meta={{
          channel: 'series',
          channelId: series._id,
        }}
        onSave={onSave}
      />
    </>
  );
};

const StepTwo = ({
  series,
  userSeriesApplication,
  onSave,
}: {
  series: Series;
  userSeriesApplication?: UserSeriesApplication;
  onSave: () => void;
}) => {
  const { track } = useAnalytics();
  const [answers, setAnswers] = useState({
    aboutEntertainer: {
      question: 'About your drag *',
      placeholder: 'Tell us about your drag...',
      answer: userSeriesApplication?.questions?.aboutEntertainer?.answer,
    },
    aboutScene: {
      question: `What do you wish vistors knew about drag in ${series.city.name}? *`,
      placeholder: `You HAVE to see drag in ${series.city.name} because...`,
      answer: userSeriesApplication?.questions?.aboutScene?.answer,
    },
  });

  const { isUpdatingSeriesApplication, updateSeriesApplicationAsync } =
    useUpdateSeriesApplicationMutation(series._id);

  const handleContinue = async () => {
    track('Submit Series Feature Step', { step: 2 });
    await updateSeriesApplicationAsync({
      questions: answers,
    });
    onSave();
  };

  const questionFields = useMemo(() => {
    return Object.entries(answers).map(([key, { question, placeholder, answer }]) => (
      <div key={key}>
        <FormLabel text={question} />
        <Textarea
          name={key}
          placeholder={placeholder}
          defaultValue={answer}
          onChange={(updatedValue) => {
            setAnswers((prevAnswers) => ({
              ...prevAnswers,
              [key]: {
                ...prevAnswers[key],
                answer: updatedValue,
              },
            }));
          }}
        />
      </div>
    ));
  }, []);

  return (
    <>
      <TitleToolbar text="Step 2: Share your story" size="sm" />

      <p className="mt-0">
        We want to properly highlight of your drag and what makes you unique! Answer some questions
        about yourself and the local drag scene in {series.city.name}.
      </p>

      <div className="d-grid gap-2">
        {questionFields}
        <Button
          data-testid="steptwo-continue-button"
          roundness="rounded"
          disabled={
            isUpdatingSeriesApplication ||
            !answers.aboutEntertainer.answer ||
            !answers.aboutScene.answer
          }
          onClick={handleContinue}
        >
          {isUpdatingSeriesApplication ? 'Submitting...' : 'Continue'}
        </Button>
      </div>
    </>
  );
};

export const EntertainerSpotlightSection: FC<{
  label: string;
  entertainerOptions: { value: string; label: string }[];
  spotlights: { entertainerId: string; entertainerTitle?: string; reason: string }[];
  index: number;
  name: string;
  updateSpotlightEntertainer: (
    index: number,
  ) => (
    updatedEntertainer:
      | { entertainerId: string; entertainerTitle?: undefined }
      | { entertainerId?: undefined; entertainerTitle: string },
  ) => void;
  updateSpotlightEntertainerReason: (index: number) => (updatedReason: string) => void;
  isCustomInputEnabled?: boolean;
}> = ({
  label,
  entertainerOptions,
  spotlights,
  index,
  name,
  updateSpotlightEntertainer,
  updateSpotlightEntertainerReason,
  isCustomInputEnabled = true,
}) => {
  const { track } = useAnalytics();
  const { entertainerId, entertainerTitle, reason } = spotlights[index] ?? {};

  const entertainer = entertainerOptions.find((option) => option.value === entertainerId);
  const [customInputIsVisible, setCustomInputIsVisible] = useState(false);

  const EntertainerSpotlightEmptyState: FC<{ searchValue: string; onClose: () => void }> = ({
    searchValue,
    onClose,
  }) => {
    return (
      <div
        key={`entertainer-spotlight-${entertainerOptions.length}`}
        className="p-2 text-align-center"
      >
        <p className="mb-0">
          <strong>{searchValue ? `No matches for "${searchValue}"` : 'No matches'}</strong>
        </p>
        {isCustomInputEnabled && (
          <>
            <p className="mt-0">
              Don't see who you're looking for? You can still give them a shoutout!
            </p>
            <Button
              variant="smooth"
              color="neutral"
              roundness="rounded"
              onClick={() => {
                track('Show Custom Input');
                setCustomInputIsVisible(true);
                updateSpotlightEntertainer(index)({
                  entertainerTitle: searchValue,
                });
                onClose();
              }}
            >
              Continue
            </Button>
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      {entertainerOptions.length && !customInputIsVisible ? (
        <MultiSelectField
          label={label}
          allowSearch
          isSingleSelect
          options={entertainerOptions}
          defaultValue={entertainerId ? [entertainerId] : []}
          placeholder="Select entertainer"
          name={`${name}Entertainer`}
          onChange={(updatedValues) => {
            track('Update Spotlight Entertainer', { index, entertainerId: updatedValues[0] });
            updateSpotlightEntertainer(index)({ entertainerId: updatedValues[0] });
          }}
          EmptyStateComponent={EntertainerSpotlightEmptyState}
        />
      ) : (
        <div>
          <FormLabel text={label} />
          <Input
            placeholder="Entertainer's name"
            name={`${name}Entertainer`}
            defaultValue={entertainerTitle}
            onChange={(event) =>
              updateSpotlightEntertainer(index)({ entertainerTitle: event.target.value })
            }
          />
        </div>
      )}
      <div className="mt-1">
        <Textarea
          disabled={!entertainer && !entertainerTitle}
          defaultValue={reason}
          name={`${name}Reason`}
          placeholder={
            entertainer?.label || entertainerTitle
              ? `(e.g., "You have not LIVED until you have seen ${entertainer?.label ?? entertainerTitle} perform live! No doubt my favorite performer in town! 🔥")`
              : ''
          }
          onChange={updateSpotlightEntertainerReason(index)}
        />
      </div>
    </div>
  );
};

const StepThree = ({
  series,
  userSeriesApplication,
  onSave,
}: {
  series: Series;
  userSeriesApplication?: UserSeriesApplication;
  onSave: () => void;
}) => {
  const { track } = useAnalytics();
  const [spotlights, setSpotlights] = useState<
    { entertainerId: string; entertainerTitle?: string; reason: string }[]
  >([
    {
      entertainerId: userSeriesApplication?.questions?.spotlightOne?.meta?.entertainerId ?? '',
      reason: userSeriesApplication?.questions?.spotlightOne?.meta?.reason ?? '',
    },
    {
      entertainerId: userSeriesApplication?.questions?.spotlightTwo?.meta?.entertainerId ?? '',
      reason: userSeriesApplication?.questions?.spotlightTwo?.meta?.reason ?? '',
    },
  ]);
  const { isFetched: areEntertainersReady, entertainers = [] } = useSeriesEntertainersQuery(
    series._id,
  );

  const entertainerOptions = useMemo(() => {
    return orderBy(
      entertainers.map((entertainer) => ({ value: entertainer._id, label: entertainer.title })),
      'label',
      'asc',
    );
  }, [entertainers]);

  const updateSpotlightEntertainer =
    (index: number) =>
    (
      updatedEntertainer:
        | { entertainerId: string; entertainerTitle?: undefined }
        | { entertainerId?: undefined; entertainerTitle: string },
    ) => {
      setSpotlights((prevValue) => {
        const updatedValue = [...prevValue];
        if (updatedEntertainer.entertainerId) {
          updatedValue[index].entertainerId = updatedEntertainer.entertainerId;
        }
        if (updatedEntertainer.entertainerTitle) {
          updatedValue[index].entertainerTitle = updatedEntertainer.entertainerTitle;
        }
        return updatedValue;
      });
    };

  const updateSpotlightEntertainerReason = (index: number) => (updatedReason: string) => {
    setSpotlights((prevValue) => {
      const updatedValue = [...prevValue];
      updatedValue[index].reason = updatedReason;
      return updatedValue;
    });
  };

  const { updateSeriesApplicationAsync } = useUpdateSeriesApplicationMutation(series._id);
  const handleSubmit = async () => {
    track('Submit Series Feature Step', { step: 3 });

    const entertainerOne = entertainerOptions.find(
      (entertainer) => entertainer.value === spotlights[0].entertainerId,
    );
    const entertainerTwo = entertainerOptions.find(
      (entertainer) => entertainer.value === spotlights[1].entertainerId,
    );

    await updateSeriesApplicationAsync({
      questions: {
        spotlightOne: {
          question: 'First entertainer spotlight',
          answer: entertainerOne.label,
          meta: {
            entertainerId: spotlights[0].entertainerId,
            reason: spotlights[0].reason,
          },
        },
        spotlightTwo: {
          question: 'Second entertainer spotlight',
          answer: entertainerTwo.label,
          meta: {
            entertainerId: spotlights[1].entertainerId,
            reason: spotlights[1].reason,
          },
        },
      },
    });

    onSave();
  };

  const hasEmptyFields =
    spotlights.length && spotlights.some(({ entertainerId, reason }) => !entertainerId || !reason);
  const isSameSpotlightEntertainer =
    spotlights?.[0]?.entertainerId &&
    spotlights?.[0]?.entertainerId === spotlights?.[1]?.entertainerId;

  return (
    <>
      <TitleToolbar text="Step 3: Endorse local artists" size="sm" />

      <p className="mt-0">
        In your feature, we'd love to highlight other local drag artists who you want to spotlight
        as well. Select two entertainers to support and give a couple reasons why!
      </p>

      {areEntertainersReady && entertainerOptions?.length ? (
        <div className="d-grid gap-2">
          <EntertainerSpotlightSection
            label="First entertainer *"
            entertainerOptions={entertainerOptions}
            spotlights={spotlights}
            index={0}
            name="spotlightOne"
            updateSpotlightEntertainer={updateSpotlightEntertainer}
            updateSpotlightEntertainerReason={updateSpotlightEntertainerReason}
          />
          {spotlights?.[0]?.entertainerId && (
            <EntertainerSpotlightSection
              label="Second entertainer *"
              entertainerOptions={entertainerOptions}
              spotlights={spotlights}
              index={1}
              name="spotlightTwo"
              updateSpotlightEntertainer={updateSpotlightEntertainer}
              updateSpotlightEntertainerReason={updateSpotlightEntertainerReason}
            />
          )}
          <Button
            data-testid="stepthree-submit-button"
            roundness="rounded"
            disabled={hasEmptyFields || isSameSpotlightEntertainer}
            onClick={handleSubmit}
          >
            Finish up!
          </Button>
          {isSameSpotlightEntertainer && (
            <p className="text-danger mt-0 text-center">
              Please select two different entertainers.
            </p>
          )}
        </div>
      ) : (
        <>
          <Skeleton height={60} />
          <Skeleton height={60} />
        </>
      )}
    </>
  );
};

const FormContent: FC<{
  series: Series;
  userSeriesApplication?: UserSeriesApplication;
  schedule: EntertainerSchedule | undefined;
  backUrl: string;
}> = ({ series, userSeriesApplication, schedule, backUrl }) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const [isComplete, setIsComplete] = useState(() =>
    Boolean(
      userSeriesApplication?.entertainerMediaId &&
      userSeriesApplication?.questions?.aboutEntertainer?.answer &&
      userSeriesApplication?.questions?.aboutScene?.answer &&
      userSeriesApplication?.questions?.spotlightOne?.answer &&
      userSeriesApplication?.questions?.spotlightTwo?.answer,
    ),
  );
  const [step, setStep] = useState<1 | 2 | 3>(() => {
    if (!userSeriesApplication?.entertainerMediaId) {
      return 1;
    }

    return 2;
  });

  useEffect(() => {
    track('Show Series Feature Form Step', { step });
  }, [step]);

  if (isComplete) {
    if (!schedule?.didSubmitThisWeek) {
      return (
        <EmptyState
          title="Application Submitted!"
          text="Thank you for applying to be featured! We will review your application. In the meantime, update your schedule so we can share your upcoming shows too!"
          button={{
            text: 'Update your schedule',
            onClick: () => {
              navigate(`/manage/entertainer/schedule`, {
                state: {
                  back: backUrl,
                },
              });
            },
          }}
        />
      );
    }

    return (
      <EmptyState
        title="Application Submitted!"
        text="Thank you for applying to be featured! We will review your application. In the meantime, check our the other local performers and support your favorites!"
        button={{
          text: 'Support local drag',
          onClick: () => {
            navigate(`/entertainers/support?cityId=${series.city._id}`);
          },
        }}
      />
    );
  }

  return (
    <>
      {step === 1 && (
        <StepOne
          series={series}
          userSeriesApplication={userSeriesApplication}
          onSave={() => setStep(2)}
        />
      )}
      {step === 2 && (
        <StepTwo
          series={series}
          userSeriesApplication={userSeriesApplication}
          onSave={() => setStep(3)}
        />
      )}
      {step === 3 && (
        <StepThree
          series={series}
          userSeriesApplication={userSeriesApplication}
          onSave={() => setIsComplete(true)}
        />
      )}
    </>
  );
};

interface SeriesFeatureFormScreenQueryParams {}

export const SeriesFeatureFormScreen = createScreenComponent<
  SeriesFeatureFormScreenProps,
  SeriesFeatureFormScreenQueryParams
>(({ tagSlug, f7route }) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();

  const { isFetched: isSeriesReady, series } = useSeriesByTagSlug(tagSlug);
  const { isUserSeriesApplicationReady, userSeriesApplication, refetchUserSeriesApplication } =
    useSeriesApplicationQuery(series?._id || '');
  const { schedule } = useCurrentEntertainerScheduleQuery();

  const backUrl = `/noauth/series/${tagSlug}?tab=postersReviews`;

  return (
    <Screen
      name="Series Feature Form Screen"
      hideToolbar
      onReenter={() => {
        if (series?._id) {
          refetchUserSeriesApplication();
        }
      }}
      headerProps={{
        back: { to: backUrl },
        title: <Screen.GagglTitle />,
        right: <ShareLinkButton url={`https://my.gaggl.app/noauth/series/${tagSlug}/apply`} />,
      }}
    >
      <Screen.Content>
        {isSeriesReady ? (
          series && (
            <>
              <div className="d-flex mb-3 gap-2 align-items-center">
                <img src={magazineSvgUrl} height={48} width={48} />
                <div>
                  <strong className="fs-5">{series.title}</strong>
                  <br />
                  <span>Share Look + Get Featured</span>
                </div>
              </div>

              <p className="mt-0">
                <em>
                  <strong>Gaggl</strong> is here to celebrate all forms local drag especially those
                  who don't always get the spotlight — kings, things, and everything in between.
                </em>
              </p>
              <p>
                Fill out the application for a spotlight, and we'll select a few entertainers each
                month to highlight our weekly show features.
              </p>

              {isUserSeriesApplicationReady ? (
                <FormContent
                  series={series}
                  userSeriesApplication={userSeriesApplication}
                  schedule={schedule}
                  backUrl={backUrl}
                />
              ) : (
                <Skeleton height={200} />
              )}
            </>
          )
        ) : (
          <>
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={80} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </>
        )}
      </Screen.Content>
    </Screen>
  );
});
